import { Component, DestroyRef, OnInit, ViewChild, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ModalDirective } from 'ngx-bootstrap/modal';
import { ToastrService } from 'ngx-toastr';
import { firstValueFrom } from 'rxjs';
import { filter, pairwise } from 'rxjs/operators';
import { AllowanceService, AllowanceSpender } from '../../services/allowance.service';
import { CommonService } from '../../services/common.service';
import { TransactionService } from '../../services/transaction.service';
import { AllowanceDecreaseRequiredPayload, SocketService } from '../../services/socket.service';
import { UserStateService } from '../../services/user-state.service';
import { WebStorageService } from '../../services/web-storage.service';
import { NgIf, DecimalPipe } from '@angular/common';
import { WalletLabelPipe } from '../../pipes/wallet-label.pipe';

@Component({
    selector: 'app-allowance-warning-modal',
    templateUrl: './allowance-warning-modal.component.html',
    styleUrls: ['./allowance-warning-modal.component.css'],
    imports: [ModalDirective, NgIf, DecimalPipe, WalletLabelPipe]
})
export class AllowanceWarningModalComponent implements OnInit {
  @ViewChild('allowanceWarningModal', { static: false }) modal?: ModalDirective;

  private readonly destroyRef = inject(DestroyRef);
  private readonly socketService = inject(SocketService);
  private readonly allowanceService = inject(AllowanceService);
  private readonly commonService = inject(CommonService);
  private readonly transactionService = inject(TransactionService);
  private readonly toastr = inject(ToastrService);
  private readonly webStorageService = inject(WebStorageService);
  private readonly userState = inject(UserStateService);

  private readonly queue: AllowanceDecreaseRequiredPayload[] = [];
  active: AllowanceDecreaseRequiredPayload | null = null;
  processing = false;

  ngOnInit(): void {
    this.socketService
      .getAllowanceDecreaseRequiredEvent()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((payload) => this.enqueue(payload));

    // Sweep allowance on login. `pairwise` only emits once user$ has
    // changed at least once — so a page refresh (single emission of the
    // restored user) is naturally skipped, but a true logout → login
    // transition (null → user) fires this sweep. Lets us show the popup
    // immediately on /dashboard right after sign-in without backend help.
    this.userState.user$
      .pipe(
        pairwise(),
        filter(([prev, curr]) => !prev?._id && !!curr?._id),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => this.sweepAllowanceOnLogin());
  }

  // Only prompt when the excess allowance is material. Anything under 5 tokens
  // isn't worth interrupting the user for — it also comfortably clears the
  // float-drift buffer in allowance.service.ts (0.001 token per layer, stacking
  // to ~2× after 2+ bids before any reduce).
  private static readonly MIN_PROMPT_AMOUNT = 5;

  // Dust tolerance for "has the excess grown since we snoozed?" — must stay at
  // drift level, not at the prompt threshold, so a genuinely larger excess
  // re-prompts instead of being swallowed by the snooze.
  private static readonly SNOOZE_GROWTH_TOLERANCE = 0.01;

  // Once the prompt is shown, snooze it for this long so cancelling OR refreshing
  // the page doesn't re-trigger it on every interaction. Keyed per wallet+currency
  // in localStorage; cleared when the user actually reduces; re-armed only if the
  // excess grows. (The excess allowance stays live during the snooze window.)
  private static readonly SNOOZE_HOURS = 6;
  private static readonly SNOOZE_STORAGE_KEY = 'rw.allowance.reduce.snooze.v1'; // betterleaks:allow — localStorage key name, not a secret

  private enqueue(payload: AllowanceDecreaseRequiredPayload): void {
    // Custodial (Fireblocks) users don't manage their own allowance — the
    // platform handles it via the co-signing flow. Suppress the prompt for
    // them so the socket emit and any other caller is short-circuited here.
    if (this.isRegulated()) return;
    // Ignore immaterial excess even when the backend still emits it — the
    // socket payload is not the source of truth for whether it's worth showing.
    if (!Number.isFinite(payload.allowanceAmount) ||
        payload.allowanceAmount < AllowanceWarningModalComponent.MIN_PROMPT_AMOUNT) return;
    // De-dupe by counter so a duplicate emit doesn't re-prompt.
    if (this.active?.counterId === payload.counterId) return;
    if (this.queue.some((item) => item.counterId === payload.counterId)) return;
    // Respect an active 6h snooze for this wallet+currency (armed when the prompt
    // was last shown, then dismissed/refreshed) — unless the excess has grown.
    if (this.isSnoozed(payload)) return;
    this.queue.push(payload);
    this.showNext();
  }

  private showNext(): void {
    if (this.active || this.processing) return;
    const next = this.queue.shift();
    if (!next) return;
    this.active = next;
    // Arm the snooze the moment the prompt is shown. This covers both Cancel and a
    // page refresh — a refresh can't run onCancel, but the snooze is already
    // persisted, so the reload won't re-show it for SNOOZE_HOURS.
    this.armSnooze(next);
    setTimeout(() => this.modal?.show(), 0);
  }

  async onConfirm(): Promise<void> {
    if (!this.active || this.processing) return;
    const payload = this.active;
    const account = this.getAccount();
    const user = this.getUser();

    if (!account?.walletAddress || !user?._id) {
      this.toastr.error('Wallet not connected. Connect your wallet to adjust allowance.');
      return;
    }

    this.processing = true;
    let txResp: any;
    try {
      const allowanceResult = await this.allowanceService.computeCounterAllowance({
        account,
        currencyId: payload.currencyId,
        currencyAddress: payload.currencyAddress,
        excludeId: payload.loanRequestId,
        counterAmountWithFeesHuman: 0,
        spender: payload.spender
      });

      if (allowanceResult.action !== 'decrease' || allowanceResult.deltaWei === 0n) {
        this.toastr.success('Allowance is already up to date.');
        this.clearSnooze(payload.currencyId, payload.spender);
        this.dismiss();
        return;
      }

      txResp = await this.transactionService.createTransaction({
        from: account.walletAddress,
        to: payload.currencyAddress,
        from_id: user._id,
        transaction_name: 'Adjust allowance',
        status: 0,
        amount: 0,
        currency_symbol: payload.currencySymbol || '-'
      });
      this.commonService.transactionEmitter({ status: true, count: 0 });

      const args = {
        functionName: 'decreaseAllowance',
        args: [allowanceResult.spenderAddress, allowanceResult.deltaWei],
        abiType: 'erc20'
      };
      let tx: any = await this.commonService.sendTransaction(
        account,
        payload.currencyAddress,
        args,
        txResp.data._id
      );
      if (this.isRegulated()) tx = JSON.parse(tx.data);

      await this.transactionService.createTransaction(
        { status: 1, transaction_hash: tx.transactionHash },
        txResp.data._id
      );
      this.commonService.transactionEmitter({ status: true, count: 1 });

      this.toastr.success('Allowance decreased successfully.');
      this.clearSnooze(payload.currencyId, payload.spender);
      this.dismiss();
    } catch (error: any) {
      this.toastr.error(error?.shortMessage || error?.message || 'Failed to decrease allowance.');
    } finally {
      this.processing = false;
      this.showNext();
    }
  }

  onCancel(): void {
    if (this.processing) return;
    this.dismiss();
    this.showNext();
  }

  private dismiss(): void {
    this.modal?.hide();
    this.active = null;
  }

  private getAccount(): any {
    const raw = this.webStorageService.getLocalStorage('account');
    if (!raw) return null;
    try { return JSON.parse(raw); } catch { return null; }
  }

  private getUser(): any {
    const raw = this.webStorageService.getLocalStorage('user');
    if (!raw) return null;
    try { return JSON.parse(raw); } catch { return null; }
  }

  private isRegulated(): boolean {
    const raw = this.webStorageService.getLocalStorage('regulated');
    if (!raw) return false;
    try { return JSON.parse(raw) === true; } catch { return false; }
  }

  // ----- Reduce-allowance prompt snooze (per wallet + currency) -----

  // Lending keeps its original key so existing snoozes still apply; the exchange gets its own,
  // so dismissing one contract's prompt never hides the other's excess in the same currency.
  private snoozeKey(currencyId: string, spender: AllowanceSpender = 'lending'): string | null {
    const wallet = this.getAccount()?.walletAddress;
    if (!wallet || !currencyId) return null;
    const base = `${String(wallet).toLowerCase()}_${currencyId}`;
    return spender === 'exchange' ? `${base}_exchange` : base;
  }

  private readSnoozeMap(): Record<string, { until: number; amount: number }> {
    try {
      const raw = this.webStorageService.getLocalStorage(AllowanceWarningModalComponent.SNOOZE_STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : null;
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
      return {};
    }
  }

  private writeSnoozeMap(map: Record<string, { until: number; amount: number }>): void {
    try {
      this.webStorageService.setLocalStorage(
        AllowanceWarningModalComponent.SNOOZE_STORAGE_KEY,
        JSON.stringify(map)
      );
    } catch {
      // best-effort: private mode / quota — never block the flow on storage failure
    }
  }

  // Suppress while a live snooze covers this wallet+currency — unless the excess
  // grew materially beyond the snoozed amount (then re-prompt). Fail-safe: any
  // error or missing entry returns false (i.e. show the prompt).
  private isSnoozed(payload: AllowanceDecreaseRequiredPayload): boolean {
    const key = this.snoozeKey(payload.currencyId, payload.spender);
    if (!key) return false;
    const entry = this.readSnoozeMap()[key];
    if (!entry || !Number.isFinite(entry.until) || entry.until <= Date.now()) return false;
    const grew = Number(payload.allowanceAmount) >
      Number(entry.amount) + AllowanceWarningModalComponent.SNOOZE_GROWTH_TOLERANCE;
    return !grew;
  }

  private armSnooze(payload: AllowanceDecreaseRequiredPayload): void {
    const key = this.snoozeKey(payload.currencyId, payload.spender);
    if (!key) return;
    const map = this.readSnoozeMap();
    map[key] = {
      until: Date.now() + AllowanceWarningModalComponent.SNOOZE_HOURS * 60 * 60 * 1000,
      amount: Number(payload.allowanceAmount) || 0
    };
    this.writeSnoozeMap(map);
  }

  private clearSnooze(currencyId: string, spender: AllowanceSpender = 'lending'): void {
    const key = this.snoozeKey(currencyId, spender);
    if (!key) return;
    const map = this.readSnoozeMap();
    if (map[key]) {
      delete map[key];
      this.writeSnoozeMap(map);
    }
  }

  private async sweepAllowanceOnLogin(): Promise<void> {
    if (this.isRegulated()) return;
    try {
      const res: any = await firstValueFrom(this.commonService.getCurrencies());
      const currencies: any[] = Array.isArray(res?.data) ? res.data : [];
      for (const currency of currencies) {
        if (!currency?._id || currency?.is_deleted) continue;
        // Loan offers and bids are approved to different contracts, so each is checked on its own.
        for (const spender of ['lending', 'exchange'] as AllowanceSpender[]) {
          try {
            const { activeCounterSum, contractAllowance } = await this.allowanceService.checkAllowance(currency._id, undefined, spender);
            const excess = Number(contractAllowance) - Number(activeCounterSum);
            if (!Number.isFinite(excess) || excess < AllowanceWarningModalComponent.MIN_PROMPT_AMOUNT) continue;
            this.enqueue({
              counterId: spender === 'exchange' ? `login_sweep_exchange_${currency._id}` : `login_sweep_${currency._id}`,
              loanRequestId: '',
              currencyId: currency._id,
              currencyAddress: currency.address,
              currencySymbol: currency.display_symbol,
              allowanceAmount: excess,
              spender
            });
          } catch {
            // skip this currency and contract on error, continue sweeping the rest
          }
        }
      }
    } catch {
      // silent — don't block other flows
    }
  }
}
